import { useState, type FormEvent } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { AuthLayout } from "../components/AuthLayout";
import { Field } from "../components/Field";
import { api, ApiRequestError } from "../api/client";

/** FR-AUTH-03 — consume the reset token and set a new password. */
export function ResetPasswordPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") ?? "";

  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [fieldError, setFieldError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  if (!token) {
    return (
      <AuthLayout
        title="Link not valid"
        subtitle="This reset link is missing its token. Request a new one."
        footer={<Link to="/login">Back to sign in</Link>}
      >
        <Link className="btn btn--primary btn--block" to="/forgot-password">
          Request a new link
        </Link>
      </AuthLayout>
    );
  }

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    setError(null);
    setFieldError(null);

    if (password !== confirm) {
      setFieldError("Both passwords must match.");
      return;
    }

    setSubmitting(true);
    try {
      await api("/auth/password-reset/confirm", {
        method: "POST",
        body: { token, password },
        auth: false,
      });
      navigate("/login?reset=1", { replace: true });
    } catch (caught) {
      if (caught instanceof ApiRequestError) {
        const fields = caught.fieldErrors();
        if (fields.password) setFieldError(fields.password);
        else setError(caught.message);
      } else {
        setError("Could not reach the server. Try again.");
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <AuthLayout
      title="Set a new password"
      subtitle="You'll be signed out everywhere else."
      footer={<Link to="/login">Back to sign in</Link>}
    >
      <form onSubmit={handleSubmit} noValidate>
        {error && (
          <div className="alert alert--error" role="alert">
            {error}
          </div>
        )}
        <Field
          label="New password"
          type="password"
          name="password"
          autoComplete="new-password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          help="At least 12 characters."
          error={fieldError ?? undefined}
          required
        />
        <Field
          label="Confirm new password"
          type="password"
          name="confirmPassword"
          autoComplete="new-password"
          value={confirm}
          onChange={(e) => setConfirm(e.target.value)}
          required
        />
        <button
          type="submit"
          className="btn btn--primary btn--block"
          disabled={submitting}
        >
          {submitting ? "Saving…" : "Set new password"}
        </button>
      </form>
    </AuthLayout>
  );
}
